/**
 * useCapacidadeRibbon — Ribbon config para Gestão de Capacidade & FTE (TeamPage)
 *
 * Grupos:
 *   LEFT  "Gestão"       → Rebalancear · Priorizar · Atualizar
 *   LEFT  "Inteligência" → Simular Cenário
 *
 * Slot slots.afterGroups → filtersWidget (TypeFilter · Sort · Search — gerenciados pela página)
 */

import { useMemo } from 'react';
import { Shuffle, Zap, RefreshCcw, FlaskConical } from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';

const NOOP = () => {};

export interface CapacidadeRibbonDeps {
  onRebalance?: () => void;
  onPrioritize?: () => void;
  onRefresh?: () => void;
  onSimulate?: () => void;
  /** Bloqueia Rebalancear se não há membros suficientes */
  canRebalance?: boolean;
  /** Widget de filtros (TypeFilter + Sort + Search) renderizado como slot */
  filtersWidget?: React.ReactNode;
}

export function useCapacidadeRibbon(deps: CapacidadeRibbonDeps): RibbonConfig {
  const {
    onRebalance = NOOP,
    onPrioritize = NOOP,
    onRefresh = NOOP,
    onSimulate = NOOP,
    canRebalance = true,
    filtersWidget,
  } = deps;

  return useMemo<RibbonConfig>(
    () => ({
      moduleId: 'manager',
      groups: [
        // ─── Gestão (LEFT) ─────────────────────────────────────────────────
        {
          id: 'gestao',
          label: 'Gestão',
          position: 'left',
          actions: [
            {
              id: 'rebalancear',
              label: 'Rebalancear',
              icon: Shuffle,
              onClick: onRebalance,
              disabled: !canRebalance,
              tooltip: canRebalance
                ? 'Distribuir carga entre operadores automaticamente'
                : 'Mínimo 2 operadores com tarefas ativas para rebalancear',
            },
            {
              id: 'priorizar',
              label: 'Priorizar',
              icon: Zap,
              onClick: onPrioritize,
              tooltip: 'Repriorizar fila de tarefas da equipe',
            },
            {
              id: 'atualizar',
              label: 'Atualizar',
              icon: RefreshCcw,
              onClick: onRefresh,
              tooltip: 'Recarregar dados de capacidade',
            },
          ],
        },
        // ─── Inteligência (LEFT) ─────────────────────────────────────────────
        {
          id: 'inteligencia',
          label: 'Inteligência',
          position: 'left',
          actions: [
            {
              id: 'simular',
              label: 'Simular',
              icon: FlaskConical,
              onClick: onSimulate,
              tooltip: 'Abrir Simulador What-If de capacidade',
            },
          ],
        },
      ],
      slots: {
        afterGroups: filtersWidget,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRebalance, onPrioritize, onRefresh, onSimulate, canRebalance, filtersWidget],
  );
}
